import React, { useState, useRef, Fragment } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery, useIsSmallScreen } from 'packages/core'
import { useUiActionsByResource } from 'hooks'
import { EidWorkflow, Dropdown, Loader } from 'packages/eid-ui'

import config from 'config'
import {
    useTheme,
    MenuItem,
    Button,
    Backdrop,
    styled,
    Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import { PasswordModal } from 'components/PasswordModal'
import { useAppState } from 'appContext'
import { handleRenderWorkflow } from 'utils'

const StyledButton = styled(Button)(({ theme, $dropdownOpen }) => ({
    position: 'relative',
    height: '31px',
    padding: '0px',
    borderRadius: '4px',
    minWidth: '32px',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
    textTransform: 'capitalize',
    backgroundColor: $dropdownOpen ? '#3f3f55' : 'transparent',
    border: 'solid 1px #d8d8dd',
    zIndex: $dropdownOpen ? '1202' : '0',
    '&:hover': {
        backgroundColor: '#3f3f55',
        color: 'white',
        cursor: 'pointer',
    },
    '&.Mui-disabled': {
        backgroundColor: '#efeff1 !important',
        cursor: 'no-drop',
        '&:hover': {
            boxShadow: 'none',
        },
    },
}))

const StyledDropdownAnchor = styled('span')({
    position: 'absolute',
    top: '40px',
    left: '115px',
})

const StyledNoActionMessage = styled(Typography)({
    padding: '0 10px',
})

const StyledBackdrop = styled(Backdrop)(() => ({
    zIndex: '1201',
}))

export const CredentialsActionsButton = ({ item, ...rest }) => {
    const theme = useTheme()
    const query = useQuery()
    const history = useHistory()
    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreen()
    const UI_ACTIONS_NOUN = ''
    const UI_ACTIONS_VERB = ''

    const disabled = item.isExpired
    const iconColor = disabled ? theme?.palette?.grey?.[900] : '#3f3f55'

    const [actionsButtonIconColor, setActionsButtonIconColor] =
        useState(iconColor)
    const [anchorEl, setAnchorEl] = useState(null)

    const { data, isInitialLoading } = useUiActionsByResource(
        item.id,
        UI_ACTIONS_NOUN,
        UI_ACTIONS_VERB,
        Boolean(anchorEl),
    )

    const openMenu = (event) => {
        setAnchorEl(event.currentTarget)
        setActionsButtonIconColor('white')
    }

    const anchorRef = useRef(null)

    const closeMenu = () => {
        setAnchorEl(null)
        setActionsButtonIconColor(iconColor)
    }

    // Trap focus within the menu and support Escape/Tab keyboard behavior
    const handleListKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            closeMenu()
            return
        }
        if (event.key === 'Tab') {
            event.preventDefault()
            const container = event.currentTarget
            const items = container.querySelectorAll('[role="menuitem"]')
            if (!items || items.length === 0) return
            const itemsArray = Array.prototype.slice.call(items)
            const activeIndex = itemsArray.indexOf(document.activeElement)
            const nextIndex = event.shiftKey
                ? activeIndex <= 0
                    ? itemsArray.length - 1
                    : activeIndex - 1
                : activeIndex === itemsArray.length - 1
                ? 0
                : activeIndex + 1
            const el = itemsArray[nextIndex]
            if (el && el.focus) el.focus()
        }
    }

    const onActionClicked = (workflowName) => {
        handleRenderWorkflow(
            [
                `SelectedResourceGuid=${item.id}`,
                `WizardAction=edititshopsetting`,
            ],
            workflowName,
            history,
        )
    }

    const [{ masterPassword }] = useAppState()
    const [open, setOpen] = useState('')

    const handleMasterUnlock = () => {
        setOpen('enterPassword')
    }

    return (
        <>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}
            <MasterLockOverlay
                showMasterLock={!masterPassword}
                handleMasterUnlock={handleMasterUnlock}
                styles={{ minHeight: 'auto' }}
                buttonStyles={{ padding: '2px', minWidth: '32px' }}
            >
                <StyledButton
                    onClick={openMenu}
                    onMouseEnter={() => {
                        setActionsButtonIconColor('white')
                    }}
                    onMouseLeave={() => {
                        !Boolean(anchorEl) &&
                            setActionsButtonIconColor(iconColor)
                    }}
                    $dropdownOpen={Boolean(anchorEl)}
                    disabled={disabled}
                    {...rest}
                    sx={{
                        '&:hover': {
                            backgroundColor: '#3f3f55',
                            cursor: 'pointer',
                        },
                    }}
                >
                    <Icon name="Actions" color={actionsButtonIconColor} />
                    <StyledDropdownAnchor ref={anchorRef} />
                </StyledButton>
                <StyledBackdrop
                    sx={{ color: theme?.palette?.common?.white }}
                    open={Boolean(anchorEl)}
                    onClick={closeMenu}
                />
                <Dropdown
                    anchorEl={anchorRef.current}
                    showCone={!isSmallScreen}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={closeMenu}
                    handleClose={closeMenu}
                    handleListKeyDown={handleListKeyDown}
                    width={'auto'}
                    listStyles={{
                        paddingBottom: '0px',
                        whiteSpace: 'normal',
                        '& > ul': {
                            paddingBottom: '7px',
                            paddingTop: '0px',
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    iconPosition={{
                        top: '-8px',
                    }}
                    disableScrollLock={false}
                >
                    {isInitialLoading && <Loader height={40} />}
                    {data && data.length > 0 ? (
                        <>
                            {data.map((d) =>
                                isSmallScreen ? (
                                    <EidWorkflow
                                        key={d.id}
                                        baseEidUrl={config.BASE_EID_URL}
                                        title={d.friendlyName}
                                        workflowName={d.requestWorkflowName}
                                        workflowParams={[
                                            `SelectedResourceID=${item.id}`,
                                        ]}
                                    >
                                        <MenuItem
                                            onClick={closeMenu}
                                            style={{ whiteSpace: 'normal' }}
                                        >
                                            {d.friendlyName}
                                        </MenuItem>
                                    </EidWorkflow>
                                ) : (
                                    <MenuItem
                                        key={d.id}
                                        onClick={() => {
                                            closeMenu()
                                            onActionClicked(
                                                d.requestWorkflowName,
                                            )
                                        }}
                                    >
                                        {d.friendlyName}
                                    </MenuItem>
                                ),
                            )}
                        </>
                    ) : data && data.length === 0 && !isInitialLoading ? (
                        <StyledNoActionMessage>
                            {t('NoActionFound')}
                        </StyledNoActionMessage>
                    ) : (
                        <></>
                    )}
                </Dropdown>
            </MasterLockOverlay>
        </>
    )
}
